import React, { useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { BLOCKER_DESCRIPTION_MAX_LENGTH, BLOCKER_NAME_MAX_LENGTH } from 'const/limits'
import {
  Button,
  ButtonGroup
} from 'react-bootstrap'
import {
  LeftSidebarView,
  ViewBody
} from 'components/Sidebar'
import withLeftSidebar, { LeftSidebarProps } from 'components/Sidebar/withLeftSidebar'
import { ErrorMessage, Formik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import requestChangeBlockerName from 'store/sagas/requestChangeBlockerName'
import requestChangeBlockerDescription from 'store/sagas/requestChangeBlockerDescription'
import {
  FormGroup,
  ControlLabel,
  HelpBlock,
  FormControl,
} from 'react-bootstrap'
import trimRichText from 'components/RichText/utils/trim'
import getCharCount from 'components/RichText/utils/getCharCount'
import RichTextEditor from 'components/RichText/Editor'
import deepEqual from 'fast-deep-equal'

type Props = LeftSidebarProps & {
  blockerId: number,
}

const EditBlocker: React.FC<Props> = ({
  leftSidebar,
  blockerId,
}) => {
  const dispatch = useDispatch()

  const intl = useIntl()

  const currentName = useSelector<any, string>(state => state.kanbo.blockers.byId[blockerId].name)
  const currentDescription = useSelector<any, any>(state => state.kanbo.blockers.byId[blockerId].description)

  const close = useCallback(() => {
    leftSidebar.pop()
  }, [])

  return (
    <LeftSidebarView
      title={<FormattedMessage id='BLOCKERS' />}
    >
      <ViewBody>
        <Formik
          initialValues={{
            name: currentName,
            description: currentDescription
          }}
          validate={({ name, description }) => {
            const errors: any = {}
  
            if (name.length > BLOCKER_NAME_MAX_LENGTH) {
              errors.name = intl.formatMessage({ id: 'BLOCKERS_NAME_TOO_LONG' }, { limit: BLOCKER_NAME_MAX_LENGTH })
            }

            if (getCharCount(description) > BLOCKER_DESCRIPTION_MAX_LENGTH) {
              errors.description = intl.formatMessage({ id: 'BLOCKERS_DESCRIPTION_TOO_LONG' }, { limit: BLOCKER_DESCRIPTION_MAX_LENGTH })
            }
        
            return errors
          }}
          onSubmit={({ name, description }) => {
            if (name !== currentName) {
              dispatch(requestChangeBlockerName({
                blockerId,
                name,
              }))
            }

            if (description) {
              const trimmedDescription = trimRichText(description)
      
              if (!deepEqual(trimmedDescription, currentDescription)) {
                dispatch(requestChangeBlockerDescription({
                  blockerId,
                  description: trimmedDescription
                }))
              }
            }

            close()
          }}
        >
          {({ values, errors, handleSubmit, handleChange, handleBlur, isSubmitting, setFieldValue }) =>
            <form onSubmit={handleSubmit}>
              <FormGroup
                validationState={errors.name ? 'error' : null}
              >
                <ControlLabel><FormattedMessage id='NAME' /></ControlLabel>
                <FormControl
                  autoFocus
                  type='text'
                  name='name'
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <ErrorMessage name='name' component={HelpBlock} />
              </FormGroup>
              <FormGroup
                validationState={errors.description ? 'error' : null}
              >
                <ControlLabel><FormattedMessage id='DESCRIPTION' /></ControlLabel>
                <RichTextEditor
                  content={values.description}
                  onChange={(content) => {
                    setFieldValue('description', content)
                  }}
                />
                {errors.description && <HelpBlock>{errors.description}</HelpBlock>}
              </FormGroup>
              <ButtonGroup>
                <Button type='submit' bsStyle='primary' disabled={isSubmitting}><FormattedMessage id='SAVE' /></Button>
                <Button onClick={close}><FormattedMessage id='CANCEL' /></Button>
              </ButtonGroup>
            </form>
          }
        </Formik>
      </ViewBody>
    </LeftSidebarView>
  )
}

export default withLeftSidebar(EditBlocker)
